import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import TestResult from './TestResult';
import { debug, error as logError, info } from '../../utils/logger';
import { useI18n } from '../../i18n';

function TestFlow() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { t } = useI18n();
  
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  // Loading indicator component
  const LoadingIndicator = () => (
    <div className="loading-indicator">
      <div className="spinner"></div>
      <p>Loading test...</p>
    </div>
  );
  
  // Error message component
  const ErrorMessage = ({ message }) => (
    <div className="error-container">
      <h3>Error</h3>
      <p>{message}</p>
      <button onClick={() => navigate('/scenarios')}>Return to Tests</button>
    </div>
  );
  
  // Get scenario data from local JSON
  useEffect(() => {
    async function fetchScenarios() {
      try {
        setLoading(true);
        setError('');
        
        // 尝试不同的路径加载数据文件
        let response;
        let data;
        let loadSuccess = false;
        
        // 尝试加载路径1
        try {
          response = await fetch('/combinedScenariosData.json');
          if (response.ok) {
            data = await response.json();
            loadSuccess = true;
            debug("成功从/combinedScenariosData.json加载数据");
          }
        } catch (e) {
          logError("路径1加载失败:", e);
        }
        
        // 如果路径1失败，尝试路径2
        if (!loadSuccess) {
          try {
            response = await fetch('/data/combinedScenariosData.json');
            if (response.ok) {
              data = await response.json();
              loadSuccess = true;
              debug("成功从/data/combinedScenariosData.json加载数据");
            }
          } catch (e) {
            logError("路径2加载失败:", e);
          }
        }
        
        // 如果路径2失败，尝试路径3
        if (!loadSuccess) {
          try {
            response = await fetch('./data/combinedScenariosData.json');
            if (response.ok) {
              data = await response.json();
              loadSuccess = true;
              debug("成功从./data/combinedScenariosData.json加载数据");
            }
          } catch (e) {
            logError("路径3加载失败:", e);
          }
        }
        
        // 如果所有路径都失败
        if (!loadSuccess) {
          throw new Error(`无法加载场景数据，请检查网络连接或刷新页面`);
        }
        
        // 确保数据包含scenarios数组
        if (!data.scenarios || !Array.isArray(data.scenarios)) {
          logError("加载的数据不包含scenarios数组:", data);
          throw new Error("数据格式错误，缺少scenarios数组");
        }
        
        info(`成功加载了${data.scenarios.length}个场景`);
        
        // Transform scenario data to match expected format
        const formattedScenarios = data.scenarios.map(scenario => {
          const options = [
            "Option A - Very appropriate response",
            "Option B - Somewhat appropriate response",
            "Option C - Somewhat inappropriate response",
            "Option D - Very inappropriate response"
          ];
          
          // 根据场景ID生成固定的答案（使用ID的哈希值）
          // 对ID取模4得到0-3之间的固定值作为正确答案
          const getFixedAnswer = (id) => {
            // 简单的字符串哈希算法
            let hash = 0;
            for (let i = 0; i < id.length; i++) {
              hash = ((hash << 5) - hash) + id.charCodeAt(i);
              hash = hash & hash; // 转换为32位整数
            }
            // 取绝对值后对4取模，确保值在0-3之间
            return Math.abs(hash % 4);
          };
          
          return {
            id: scenario.id,
            title: scenario.title_en,
            description: scenario.scenario_en,
            options: options,
            correct_answer: getFixedAnswer(scenario.id), // 使用固定生成的答案
            is_free: !scenario.is_premium,
            feedback: scenario.expert_advice_en
          };
        });
        
        // Single scenario test
        if (scenarioId) {
          const selectedScenario = formattedScenarios.find(s => s.id === scenarioId);
          
          if (!selectedScenario) {
            setError('Scenario not found');
            return;
          }
          
          // 检查用户是否有权限访问高级内容
          const hasPremiumAccess = userData && userData.valid_subscription;
          
          if (!selectedScenario.is_free && !hasPremiumAccess) {
            setError('This is a premium scenario. Please upgrade to access.');
            return;
          }
          
          setScenarios([selectedScenario]);
          setSelectedOptions([undefined]);
        } else {
          // Complete test with multiple scenarios
          let availableScenarios = formattedScenarios;
          
          // 使用userData中的valid_subscription判断权限
          const hasPremiumAccess = userData && userData.valid_subscription;
          
          // Filter premium content for non-premium users
          if (!hasPremiumAccess) {
            availableScenarios = formattedScenarios.filter(s => s.is_free);
          }
          
          // Only keep up to 6 scenarios for testing
          const limitedScenarios = availableScenarios.slice(0, 6);
          
          if (limitedScenarios.length === 0) {
            setError('No test scenarios available');
            return;
          }
          
          // Initialize selection array with undefined values
          const initialSelections = new Array(limitedScenarios.length).fill(undefined);
          setSelectedOptions(initialSelections);
          setScenarios(limitedScenarios);
        }
      } catch (err) {
        logError('加载场景时出错:', err);
        setError('加载测试失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    }
    
    fetchScenarios();
  }, [scenarioId, currentUser, userData]);
  
  // Handle option selection
  const handleOptionSelect = (optionIndex) => {
    debug(`为问题${currentStep + 1}选择选项${optionIndex}`);
    
    const newSelectedOptions = [...selectedOptions];
    newSelectedOptions[currentStep] = optionIndex;
    setSelectedOptions(newSelectedOptions);
    
    debug('已更新选择:', newSelectedOptions);
  };
  
  // Next step
  const handleNext = () => {
    debug(`从问题${currentStep + 1}转到${currentStep + 2}`);
    
    if (currentStep < scenarios.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Show results after completing all steps
      info('测试已完成。显示带有以下选择的结果:', selectedOptions);
      setShowResults(true);
    }
  };
  
  // Previous step
  const handlePrevious = () => {
    debug(`从问题${currentStep + 1}回到${currentStep}`);
    
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  if (loading) {
    return <LoadingIndicator />;
  }
  
  if (error) {
    return <ErrorMessage message={error} />;
  }
  
  if (scenarios.length === 0) {
    return <ErrorMessage message="Could not retrieve test scenario data" />;
  }
  
  if (showResults) {
    console.log('Rendering results with:', {
      scenarios: scenarios,
      selectedOptions: selectedOptions
    });
    
    return (
      <TestResult 
        scenarios={scenarios} 
        selectedOptions={selectedOptions}
      />
    );
  }
  
  const currentScenario = scenarios[currentStep];
  
  // Ensure options array exists, provide defaults if not
  const options = currentScenario.options || [
    "Option 1 - Default",
    "Option 2 - Default",
    "Option 3 - Default",
    "Option 4 - Default"
  ];

  console.log(`Rendering question ${currentStep + 1}: ${currentScenario.title}`);
  console.log(`Current selection for this question: ${selectedOptions[currentStep] !== undefined ? selectedOptions[currentStep] : 'none'}`);
  
  return (
    <div className="test-container">
      <h2>{t('test.test_flow.title')}</h2>
      
      <div className="progress-indicator">
        {t('test.question', { current: currentStep + 1, total: scenarios.length })}
      </div>
      
      <h3>{currentScenario.title}</h3>
      
      <div className="scenario-description">
        <p>{currentScenario.description}</p>
      </div>
      
      <div className="options-container">
        {options.map((option, index) => (
          <div 
            key={index} 
            className={`option ${selectedOptions[currentStep] === index ? 'selected' : ''}`}
            onClick={() => handleOptionSelect(index)}
          >
            <span className="option-letter">{String.fromCharCode(65 + index)}.</span> {option}
          </div>
        ))}
      </div>
      
      <div className="navigation-buttons">
        <button 
          onClick={handlePrevious} 
          disabled={currentStep === 0}
        >
          {t('test.previous')}
        </button>
        
        <button 
          onClick={handleNext}
          disabled={selectedOptions[currentStep] === undefined}
        >
          {currentStep < scenarios.length - 1 ? t('test.next') : t('test.finish')}
        </button>
      </div>
    </div>
  );
}

export default TestFlow; 